/**
 * SidebarNavItem Component
 * Individual navigation item in the Sidebar, rendered as a Link
 */
import type React from 'react';
import { memo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BADGE_MAX_VALUE, NAV_ITEM_ICON_SIZE } from '@/constants/appShell';
import type { SidebarNavItemProps } from './Sidebar.types';
import styles from './Sidebar.module.css';

export const SidebarNavItem = memo(function SidebarNavItem({
  item,
  isActive,
  href,
  onNavItemClick,
}: SidebarNavItemProps) {
  const { t } = useTranslation();
  const IconComponent = item.icon;

  const { badge } = item;
  const badgeText =
    badge !== undefined && badge > 0
      ? badge > BADGE_MAX_VALUE
        ? '99+'
        : String(badge)
      : null;

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLAnchorElement>) => {
      if (isActive) {
        event.preventDefault();
      }
      onNavItemClick?.(item.id, isActive);
    },
    [isActive, onNavItemClick, item.id]
  );

  return (
    <Link
      to={href}
      className={`${styles.navItem} ${isActive ? styles.navItemActive : ''}`}
      aria-current={isActive ? 'page' : undefined}
      onClick={handleClick}
    >
      <span className={styles.navItemIcon}>
        <IconComponent size={NAV_ITEM_ICON_SIZE} />
      </span>
      <span className={styles.navItemLabel}>{t(item.labelKey)}</span>
      {badgeText !== null && (
        <span className={styles.badge} aria-label={`${badge} items`}>
          {badgeText}
        </span>
      )}
    </Link>
  );
});
